import React, { useState } from 'react';
import { AlertCircle, Play, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';
import PerformanceMetricsDisplay from './PerformanceMetricsDisplay';
import NotesSection from './NotesSection';
import GoalsSection from './GoalsSection';
import CompleteReviewModal from './CompleteReviewModal';

/**
 * Performance Review Detail - Full review view with metrics, notes, goals
 */
export default function PerformanceReviewDetail({ review, onBack, currentUser }) {
  const [reviewData, setReviewData] = useState(review);
  const [loading, setLoading] = useState(false);
  const [periodMonths, setPeriodMonths] = useState(3);
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  const handleStartReview = async () => {
    setLoading(true);
    try {
      const res = await api.post('/admin/performance/reviews/start', {
        review_id: reviewData.id,
        reviewer_id: currentUser?.id || 'admin',
        period_months: periodMonths
      });
      
      if (res.data.success) {
        setReviewData(res.data.review);
        toast.success('Review started - metrics loaded');
      }
    } catch (err) {
      toast.error('Failed to start review');
    } finally {
      setLoading(false);
    }
  };

  const handleNoteAdded = (note) => {
    setReviewData(prev => ({
      ...prev,
      notes: [...(prev.notes || []), note]
    }));
  };

  const handleNoteDeleted = (noteId) => {
    setReviewData(prev => ({
      ...prev,
      notes: (prev.notes || []).filter(n => n.id !== noteId)
    }));
  };

  const handleGoalAdded = (goal) => {
    setReviewData(prev => ({
      ...prev,
      goals: [...(prev.goals || []), goal]
    }));
  };

  const handleGoalUpdated = (goalId, updates) => {
    setReviewData(prev => ({
      ...prev,
      goals: (prev.goals || []).map(g => g.id === goalId ? { ...g, ...updates } : g)
    }));
  };

  const handleGoalDeleted = (goalId) => {
    setReviewData(prev => ({
      ...prev,
      goals: (prev.goals || []).filter(g => g.id !== goalId)
    }));
  };

  const handleReviewCompleted = (updates) => {
    setReviewData(prev => ({ ...prev, ...updates }));
    setShowCompleteModal(false);
    toast.success('Review completed!');
  };

  const metrics = reviewData.metrics;

  return (
    <div className="p-6" data-testid="performance-review-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
        <Button variant="outline" onClick={onBack} data-testid="back-to-list-btn">
          ← Back
        </Button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Performance Review: {reviewData.staff_name}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {reviewData.review_type.charAt(0).toUpperCase() + reviewData.review_type.slice(1)} Review - 
            Status: <span className="capitalize font-medium">{reviewData.status.replace(/_/g, ' ')}</span>
          </p>
        </div>
        
        {/* Complete button for in-progress reviews */}
        {reviewData.status === 'in_progress' && (
          <Button
            onClick={() => setShowCompleteModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="complete-review-btn"
          >
            Complete Review
          </Button>
        )}
      </div>

      {/* Not Started State */}
      {reviewData.status === 'scheduled' && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900 dark:text-amber-300">Review Not Started</h3>
              <p className="text-amber-700 dark:text-amber-400 mt-1">
                Click &quot;Start Review&quot; to unlock performance metrics for this staff member.
                Select the review period to analyze.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-4">
                <select
                  value={periodMonths}
                  onChange={(e) => setPeriodMonths(Number(e.target.value))}
                  className="px-3 py-2 border border-amber-300 dark:border-amber-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value={1}>Last 1 Month</option>
                  <option value={3}>Last 3 Months</option>
                  <option value={6}>Last 6 Months</option>
                  <option value={12}>Last 12 Months</option>
                </select>
                <Button 
                  onClick={handleStartReview}
                  disabled={loading}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="start-review-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                  Start Review
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Metrics Section - Only shown after review starts */}
      {metrics && (
        <div className="space-y-6">
          <PerformanceMetricsDisplay metrics={metrics} />
          
          <NotesSection 
            reviewData={reviewData}
            onNoteAdded={handleNoteAdded}
            onNoteDeleted={handleNoteDeleted}
          />
          
          <GoalsSection 
            reviewData={reviewData}
            onGoalAdded={handleGoalAdded}
            onGoalUpdated={handleGoalUpdated}
            onGoalDeleted={handleGoalDeleted}
            metrics={metrics}
          />
        </div>
      )}

      {/* Completed Review Summary */}
      {(reviewData.status === 'pending_acknowledgment' || reviewData.status === 'completed') && (
        <div className="mt-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6">
          <h3 className="font-semibold text-green-900 dark:text-green-300 mb-2">Review Completed</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-green-700 dark:text-green-400">Overall Rating</p>
              <p className="text-2xl font-bold text-green-800 dark:text-green-300">
                {reviewData.overall_rating}/5 ★
              </p>
            </div>
            <div>
              <p className="text-sm text-green-700 dark:text-green-400">Completed</p>
              <p className="text-green-800 dark:text-green-300">
                {reviewData.completed_at ? new Date(reviewData.completed_at).toLocaleDateString() : '-'}
              </p>
            </div>
          </div>
          {reviewData.summary && (
            <div className="mt-4">
              <p className="text-sm text-green-700 dark:text-green-400 mb-1">Summary</p>
              <p className="text-green-800 dark:text-green-300">{reviewData.summary}</p>
            </div>
          )}
        </div>
      )}

      {/* Complete Review Modal */}
      {showCompleteModal && (
        <CompleteReviewModal
          reviewId={reviewData.id}
          onClose={() => setShowCompleteModal(false)}
          onComplete={handleReviewCompleted}
        />
      )}
    </div>
  );
}
